import React from "react";
import { IOrderConfirmationData } from "../../models/epcCustomer.model";
import { IStateDistrict } from "../../models/LeadData.model";
import { IQuotationItem } from "../../models/quotationData.model";
import Button, { ButtonType } from "../../components/Assets/Button";

interface ConfirmOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  orderData: IOrderConfirmationData;
  setOrderData: React.Dispatch<React.SetStateAction<IOrderConfirmationData>>;
  isSubmitting: boolean;
  statesData: IStateDistrict[];
  items: IQuotationItem[];
}

const OptimizedConfirmOrderModal: React.FC<ConfirmOrderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  orderData,
  setOrderData,
  isSubmitting,
  statesData,
  items,
}) => {
  if (!isOpen) return null;

  const isDisabled = (fieldName: string) => {
    return !["gstin"].includes(fieldName) && !fieldName.startsWith("shipping");
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    type: "billing" | "shipping",
  ) => {
    const { name, value } = e.target;
    setOrderData((prev) => {
      const next = {
        ...prev,
        [`${type}Address`]: {
          ...prev[`${type}Address`],
          [name]: value,
        },
      } as typeof prev;

      // If billing address changed, uncheck "sameAsBilling"
      if (type === "billing") {
        next.sameAsBilling = false;
      }

      return next;
    });
  };

  const handleSameAsBillingChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = e.target.checked;
    setOrderData((prev) => ({
      ...prev,
      sameAsBilling: checked,
      shippingAddress: checked ? prev.billingAddress : prev.shippingAddress,
    }));
  };

  return (
    <div className="fixed inset-0 p-4 text-xs flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-[600px] max-w-full max-h-[80vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg md:text-xl font-bold text-gray-800">
            Confirm Order
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="close Confirm Modal"
          >
            <svg
              className="w-6 h-6 fill-current"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M14.348 5.652a.5.5 0 0 0-.707 0L10 9.293 6.36 5.652a.5.5 0 1 0-.707.707L9.293 10l-3.64 3.64a.5.5 0 0 0 .707.707L10 10.707l3.64 3.64a.5.5 0 0 0 .707-.707L10.707 10l3.64-3.64a.5.5 0 0 0 0-.708z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4 mb-4">
          <label className="text-gray-700 font-medium">Goods</label>
          <div className="flex flex-col gap-2">
            {items.map((item, i) => {
              const isMaterial = item.type === "material";
              const detail = isMaterial ? item.rawMaterial : item.product;

              const displayName =
                item.productName ||
                (isMaterial
                  ? (detail as any)?.name
                  : (detail as any)?.productName) ||
                "Unknown Item";

              const key =
                (typeof detail === "object" && detail !== null
                  ? detail._id
                  : detail) ||
                item._id ||
                i;

              return (
                <div
                  key={key}
                  className="flex justify-between items-center border-b pb-2 last:border-0"
                >
                  {/* LEFT: Product Name */}
                  <span className="font-medium text-gray-800">
                    {displayName}
                  </span>

                  {/* RIGHT: Quantity + Unit */}
                  <span className="text-gray-600 font-medium">
                    {item.quantity} {item.unit ? item.unit : "Units"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-4 ">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Transport Price
              </label>
              <input
                type="number"
                name="transportPrice"
                value={orderData.transportPrice}
                onChange={handleInputChange}
                disabled={isDisabled("transportPrice")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("transportPrice")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Discount Price
              </label>
              <input
                type="number"
                name="discountPrice"
                value={orderData.discountPrice}
                onChange={handleInputChange}
                disabled={isDisabled("discountPrice")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("discountPrice")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={orderData.name}
                onChange={handleInputChange}
                disabled={isDisabled("name")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("name") ? "opacity-50 cursor-not-allowed" : ""
                }`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={orderData.email}
                onChange={handleInputChange}
                disabled={isDisabled("email")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("email") ? "opacity-50 cursor-not-allowed" : ""
                }`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobileNumber"
                value={orderData.mobileNumber}
                onChange={handleInputChange}
                disabled={isDisabled("mobileNumber")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("mobileNumber")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-medium mb-2">Billing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="line1"
                value={orderData.billingAddress.line1}
                onChange={(e) => handleAddressChange(e, "billing")}
                disabled={isDisabled("billingAddress.line1")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("billingAddress.line1")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              />

              <select
                name="state"
                value={orderData.billingAddress.state.toLowerCase()}
                onChange={(e) => handleAddressChange(e, "billing")}
                disabled={isDisabled("billingAddress.state")}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("billingAddress.state")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <option value="">Select State</option>
                {statesData?.map((s) => (
                  <option key={s.state} value={s.state.toLowerCase()}>
                    {s.state.capitalizeWords()}
                  </option>
                ))}
              </select>

              <select
                name="city"
                value={orderData.billingAddress.city.toLowerCase()}
                onChange={(e) => handleAddressChange(e, "billing")}
                disabled={!orderData.billingAddress.state}
                className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                  isDisabled("billingAddress.city")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <option value="">Select City</option>
                {statesData
                  .find(
                    (s) =>
                      s.state === orderData.billingAddress.state.toLowerCase(),
                  )
                  ?.city?.map((city: string) => (
                    <option key={city} value={city}>
                      {city.capitalizeWords()}
                    </option>
                  ))}
              </select>
              <div>
                <input
                  type="text"
                  name="pincode"
                  value={orderData.billingAddress.pincode}
                  onChange={(e) => handleAddressChange(e, "billing")}
                  disabled={isDisabled("billingAddress.pincode")}
                  className={`w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500 ${
                    isDisabled("billingAddress.pincode")
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}

          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="sameAsBilling"
              checked={orderData.sameAsBilling}
              onChange={handleSameAsBillingChange}
              className="mr-2 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="sameAsBilling" className="text-xs text-gray-700">
              Shipping address same as billing
            </label>
          </div>

          {!orderData.sameAsBilling && (
            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Shipping Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="line1"
                  value={orderData.shippingAddress.line1}
                  onChange={(e) => handleAddressChange(e, "shipping")}
                  className="py-2 px-3  border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500"
                />

                <select
                  name="state"
                  value={orderData.shippingAddress.state.toLowerCase()}
                  onChange={(e) => handleAddressChange(e, "shipping")}
                  className="py-2 px-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select State</option>
                  {statesData?.map((s) => (
                    <option key={s.state} value={s.state.toLowerCase()}>
                      {s.state.capitalizeWords()}
                    </option>
                  ))}
                </select>

                <select
                  name="city"
                  value={orderData.shippingAddress.city.toLowerCase()}
                  onChange={(e) => handleAddressChange(e, "shipping")}
                  disabled={!orderData.shippingAddress.state}
                  className="py-2 px-3  border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select City</option>
                  {statesData
                    .find(
                      (s) =>
                        s.state ===
                        orderData.shippingAddress.state.toLowerCase(),
                    )
                    ?.city?.map((city: string) => (
                      <option key={city} value={city}>
                        {city.capitalizeWords()}
                      </option>
                    ))}
                </select>
                <div>
                  <input
                    type="text"
                    name="pincode"
                    value={orderData.shippingAddress.pincode}
                    onChange={(e) => handleAddressChange(e, "shipping")}
                    className="w-full py-2 px-3 border border-gray-300 rounded-lg bg-gray-100 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              GSTIN (22AAAAA0000A1Z5)
            </label>
            <input
              type="text"
              name="gstin"
              value={orderData.gstin}
              placeholder="Enter GSTIN"
              onChange={handleInputChange}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col md:flex-row justify-end gap-2 mt-6">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              Confirm Order
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizedConfirmOrderModal;
